import type { ComponentType, ReactNode } from 'react';

import * as Icons from 'lucide-react';

/**
 * Custom football-native badge icons.
 *
 * Only badges whose lucide icon was off-domain are overridden here; every other
 * badge keeps rendering the lucide icon named in badge_definitions.icon.
 *
 * Drawn in lucide's own geometry (24x24 viewBox, 2px stroke, round caps/joins,
 * stroke="currentColor") so they inherit the existing metal colour classes
 * exactly like the icons they replace. Keyed by badge_definitions.code.
 *
 * Resolution order: custom SVG -> lucide icon from badge_definitions.icon ->
 * Award. The database is not modified.
 */

interface IconProps {
  className?: string;
}

const svg =
  (body: ReactNode): ComponentType<IconProps> =>
  ({ className }: IconProps) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {body}
    </svg>
  );

export const BADGE_ICONS: Record<string, ComponentType<IconProps>> = {
  // the leap — ball rising above the climb (was: MoveUp arrow)
  aerial: svg(
    <>
      <circle cx="12" cy="5.4" r="3.4" />
      <path d="M5.4 14.6L12 11l6.6 3.6" />
      <path d="M5.4 20L12 16.4l6.6 3.6" />
    </>,
  ),

  // shoulder charge — driving forward through contact (was: Anchor)
  bull: svg(
    <>
      <circle cx="7" cy="5.2" r="2.5" />
      <path d="M4.6 9.4c-1.4 3.2-1.2 7.4.4 11.4h7.2c1.2-4 .6-8.2-2-11.4z" />
      <path d="M15.4 11h3.6M16.4 15h4.2M15.2 19h3.2" />
    </>,
  ),

  // boot in profile, studs + impact spark (was: Gavel)
  enforcer: svg(
    <>
      <path d="M2.5 9.5h5.2l3 2.4h6.8a3.5 3.5 0 0 1 3.5 3.5v2.4H2.5z" />
      <path d="M5.5 17.8v2.4M11 17.8v2.4M16.5 17.8v2.4" />
      <path d="M6.6 6.4L5.4 4M9.8 6L10 3.4M3.4 7.4L1.6 5.8" />
    </>,
  ),

  // passing triangle on the tactics board (was: Music note)
  maestro: svg(
    <>
      <circle cx="4.6" cy="17.6" r="1.9" />
      <circle cx="19.4" cy="17.6" r="1.9" />
      <circle cx="12" cy="4.6" r="1.9" />
      <path d="M6.5 17.6h11" />
      <path d="M5.6 15.9L11 6.4M13 6.4l5.4 9.5" />
    </>,
  ),
};

interface BadgeIconProps {
  /** badge_definitions.code — selects the custom football icon, when one exists */
  code: string;
  /** badge_definitions.icon — lucide name, used for every non-overridden badge */
  icon?: string;
  className?: string;
}

export function BadgeIcon({ code, icon, className }: BadgeIconProps) {
  const Custom = BADGE_ICONS[code];

  if (Custom) return <Custom className={className} />;

  const Fallback =
    (icon &&
      (Icons as unknown as Record<string, ComponentType<{ className?: string }>>)[icon]) ||
    Icons.Award;

  return <Fallback className={className} />;
}
